<template>
  <!--
    Unmatched Shortform Uploads panel.

    Sits at the top of the Promo / TVC / Filler / GFX tabs in ManageMedia.
    Surfaces files dropped in shortform watchfolders that the traffic agent
    couldn't auto-match to a media row (s3_upload_detections.llm_match_status
    = 'no_match'). Operator clicks Create-from-file to spin up a media row
    pre-filled with the source link.

    Backend endpoints (existing — see Commit 2):
      GET  /api/media/unmatched-shortform-uploads[?storage_config_id=]
      POST /api/media/from-detection

    Emits 'media-created' so the parent (ManageMedia) can refresh its main
    table when a new row appears.
  -->
  <div class="unmatched-uploads-panel">
    <!-- Collapsed header — always visible, click toggles expansion -->
    <div
      class="panel-header"
      :class="{ 'is-empty': !loading && uploads.length === 0 }"
      @click="toggleExpanded"
    >
      <span class="chevron">{{ expanded ? '▼' : '▶' }}</span>
      <strong>Unmatched Uploads</strong>
      <b-tag rounded :type="badgeType" class="ml-2">
        {{ loading ? '...' : uploads.length }}
      </b-tag>
      <span class="header-hint">
        files in {{ mediaType }} watchfolders the traffic agent couldn't match
      </span>
      <b-button
        v-if="expanded"
        size="is-small"
        class="ml-auto"
        @click.stop="loadUploads"
        :loading="loading"
        icon-right="refresh"
      >
        Refresh
      </b-button>
    </div>

    <!-- Expanded body -->
    <div v-if="expanded" class="panel-body">
      <div v-if="loading" class="has-text-centered" style="padding: 24px 0;">
        <b-loading :is-full-page="false" :active="true" />
      </div>
      <div
        v-else-if="error"
        class="has-text-centered has-text-danger"
        style="padding: 16px 0;"
      >
        {{ error }}
      </div>
      <div
        v-else-if="uploads.length === 0"
        class="has-text-centered has-text-grey"
        style="padding: 16px 0;"
      >
        No unmatched files — when editors deliver new shortform that BMS
        doesn't recognise, they'll appear here.
      </div>
      <table v-else class="unmatched-table">
        <thead>
          <tr>
            <th>Filename</th>
            <th class="num">Duration</th>
            <th>Detected</th>
            <th>Watcher</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in uploads" :key="row.detection_id">
            <td>
              <code class="filename">{{ row.filename }}</code>
            </td>
            <td class="num">
              <span v-if="row.probed_duration_ms">
                {{ (row.probed_duration_ms / 1000).toFixed(2) }}s
              </span>
              <span v-else class="has-text-grey-light">—</span>
            </td>
            <td>{{ formatRelative(row.detected_at) }}</td>
            <td>
              <span v-if="row.watch_prefix" class="has-text-grey">
                {{ row.watch_prefix }}
              </span>
            </td>
            <td>
              <b-button
                size="is-small"
                type="is-primary"
                @click="openCreateModal(row)"
              >
                Create
              </b-button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Create-from-file modal -->
    <b-modal
      :active.sync="showCreateModal"
      trap-focus
      has-modal-card
      :can-cancel="['escape', 'outside']"
      @close="closeCreateModal"
    >
      <div class="modal-card" style="width: 640px;">
        <header class="modal-card-head">
          <p class="modal-card-title">
            Create {{ formMediaTypeLabel }} from upload
          </p>
          <button class="delete" @click="closeCreateModal"></button>
        </header>
        <section class="modal-card-body">
          <div v-if="modalDetection" class="modal-content-inner">
            <p class="has-text-grey is-size-7 has-margin-bottom-10">
              <strong>Source file:</strong>
              <code>{{ modalDetection.filename }}</code>
            </p>

            <b-field label="Title *" :type="form.title ? '' : 'is-danger'">
              <b-input
                v-model="form.title"
                placeholder="Edit the suggested title"
                ref="titleInput"
              />
            </b-field>

            <div class="columns">
              <div class="column is-one-third">
                <b-field label="Type *" :type="form.mediaType ? '' : 'is-danger'">
                  <b-select
                    v-model="form.mediaType"
                    placeholder="Select type"
                    expanded
                  >
                    <option value="tvc">TVC</option>
                    <option value="promo">Promo</option>
                    <option value="filler">Filler</option>
                    <option value="gfx">GFX</option>
                    <option value="bumper">Bumper</option>
                  </b-select>
                </b-field>
              </div>
              <div class="column is-one-third">
                <b-field label="Channel *" :type="form.channelId ? '' : 'is-danger'">
                  <b-select
                    v-model="form.channelId"
                    placeholder="Select a channel"
                    expanded
                    @input="onChannelSelected"
                  >
                    <option
                      v-for="c in availableChannels"
                      :key="c.id"
                      :value="c.id"
                    >{{ c.name }}</option>
                  </b-select>
                </b-field>
              </div>
              <div class="column is-one-third">
                <b-field label="Supplier *" :type="form.supplier ? '' : 'is-danger'">
                  <b-input
                    v-model="form.supplier"
                    placeholder="Defaults to channel name"
                  />
                </b-field>
              </div>
            </div>

            <p
              v-if="form.mediaType && form.mediaType !== mediaType"
              class="has-text-warning is-size-7"
              style="margin-top: -8px; margin-bottom: 8px;"
            >
              <b-icon icon="alert" size="is-small" />
              Type changed from "{{ tabMediaTypeLabel }}" to
              "{{ formMediaTypeLabel }}" — the file will be saved under the
              {{ formMediaTypeLabel }} folder on the playout share, not the
              current tab's folder.
            </p>

            <div class="columns">
              <div class="column is-half">
                <b-field label="Duration (ms)">
                  <b-input
                    v-model.number="form.duration_ms"
                    type="number"
                    placeholder="Optional"
                  />
                </b-field>
              </div>
              <div class="column is-half">
                <b-field label="Description">
                  <b-input
                    v-model="form.description"
                    placeholder="Optional"
                  />
                </b-field>
              </div>
            </div>

            <p class="has-text-grey-light is-size-7" style="margin-top: 8px;">
              Title, Channel and Supplier are required. The source file link
              is preserved automatically. Duration is soft-required for
              scheduling — you can leave it blank and set it later.
            </p>
          </div>
        </section>
        <footer class="modal-card-foot" style="justify-content: flex-end;">
          <button class="button" @click="closeCreateModal" :disabled="creating">
            Cancel
          </button>
          <button
            class="button is-primary"
            :class="{ 'is-loading': creating }"
            :disabled="!canSubmit || creating"
            @click="submitCreate"
          >
            Create
          </button>
        </footer>
      </div>
    </b-modal>
  </div>
</template>

<script>
import MediaApi from '@/api/media'

export default {
  name: 'UnmatchedShortformUploadsPanel',
  props: {
    // Current shortform tab's media_type — passed in from ManageMedia so
    // the Create modal stamps the right value. promo / filler / tvc / gfx.
    mediaType: {
      type: String,
      required: true
    },
    // Optional: scope the unmatched list to a single watcher when the
    // operator filters by channel. Null = all watchers for the org.
    storageConfigId: {
      type: Number,
      default: null
    },
    // Auto-load on mount. Set false if the parent wants manual control.
    autoLoad: {
      type: Boolean,
      default: true
    },
    // Full channel objects [{id, name, ...}] the operator has access to.
    // Used by the Create modal's Channel dropdown. The id is required so
    // we can send the backend a proper channels: [{id, name}] payload.
    availableChannels: {
      type: Array,
      default: () => []
    },
    // If the parent's channel filter is set, pre-select that channel in
    // the modal so the operator doesn't have to pick it again.
    preselectedChannelName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      uploads: [],
      loading: false,
      error: null,
      expanded: false, // collapsed by default — operator opens when interested
      showCreateModal: false,
      modalDetection: null,
      creating: false,
      form: {
        title: '',
        duration_ms: null,
        supplier: '',
        description: '',
        channelId: null, // populated from availableChannels via dropdown
        mediaType: null  // defaults to tab mediaType in openCreateModal; operator can override
      }
    }
  },
  computed: {
    badgeType() {
      if (this.loading || this.uploads.length === 0) return 'is-light'
      return 'is-warning'
    },
    // Label for the tab's mediaType (parent prop) — used in the warning
    // when operator overrides the type at create time.
    tabMediaTypeLabel() {
      return this._labelForType(this.mediaType)
    },
    // Label for the form's currently-selected type — used in the modal
    // header and warning. Falls back to tab label until operator picks.
    formMediaTypeLabel() {
      return this._labelForType(this.form.mediaType || this.mediaType)
    },
    // Title + Type + Channel + Supplier are hard-required. Duration stays
    // soft-required (Save allowed without; schedule-drop refuses).
    canSubmit() {
      return (
        !!this.form.title && !!this.form.title.trim() &&
        !!this.form.mediaType &&
        !!this.form.channelId &&
        !!this.form.supplier && !!this.form.supplier.trim()
      )
    }
  },
  watch: {
    // Reload when the parent switches tabs (mediaType prop changes) or
    // changes channel filter (storageConfigId changes).
    mediaType() {
      if (this.expanded) this.loadUploads()
    },
    storageConfigId() {
      if (this.expanded) this.loadUploads()
    }
  },
  mounted() {
    if (this.autoLoad) this.loadCount()
  },
  methods: {
    /**
     * Light load — fetch the count without expanding the panel, so the
     * badge shows a real number when collapsed. Same endpoint as full
     * load; we just don't render the table.
     */
    async loadCount() {
      this.loading = true
      this.error = null
      try {
        const data = await MediaApi.getUnmatchedShortformUploads(this.storageConfigId)
        this.uploads = data.uploads || []
      } catch (err) {
        this.error = this._errorMessage(err)
        this.uploads = []
      } finally {
        this.loading = false
      }
    },
    async loadUploads() {
      // Identical to loadCount today, kept as a separate method so future
      // work (e.g. pagination, sort options) can diverge them cleanly.
      await this.loadCount()
    },
    toggleExpanded() {
      this.expanded = !this.expanded
      if (this.expanded && !this.loading) this.loadUploads()
    },
    openCreateModal(detection) {
      this.modalDetection = detection
      // Pre-select channel: use the parent's filter if set, otherwise the
      // only channel if the operator has just one, otherwise leave blank
      // and force them to pick.
      let preselectedId = null
      if (this.preselectedChannelName && this.availableChannels.length) {
        const match = this.availableChannels.find(
          c => c.name === this.preselectedChannelName
        )
        if (match) preselectedId = match.id
      } else if (this.availableChannels.length === 1) {
        preselectedId = this.availableChannels[0].id
      }
      // Supplier defaults to the pre-selected channel's name (BMS convention:
      // supplier name == channel name). Operator can still override.
      const preselectedChannel = preselectedId
        ? this.availableChannels.find(c => c.id === preselectedId)
        : null
      this.form = {
        title: detection.suggested_title || '',
        duration_ms: detection.probed_duration_ms || null,
        supplier: preselectedChannel ? preselectedChannel.name : '',
        description: '',
        channelId: preselectedId,
        // Default to the tab's mediaType; operator can override via the
        // Type dropdown if the file actually belongs to a different
        // shortform category (e.g. a BUMPER found in the FILLER watcher).
        mediaType: this.mediaType
      }
      this.showCreateModal = true
      // Focus the title field after the modal renders
      this.$nextTick(() => {
        if (this.$refs.titleInput && this.$refs.titleInput.focus) {
          this.$refs.titleInput.focus()
        }
      })
    },
    // When the operator picks a channel from the dropdown, auto-fill the
    // Supplier field if empty (or if it still matches the previous channel
    // name, since the typical case is supplier follows channel).
    onChannelSelected(newChannelId) {
      if (!newChannelId) return
      const ch = this.availableChannels.find(c => c.id === newChannelId)
      if (!ch) return
      // Auto-fill only when the supplier field is empty — don't overwrite
      // an operator-typed value. Power-users who deliberately set a
      // different supplier keep their value.
      if (!this.form.supplier || !this.form.supplier.trim()) {
        this.form.supplier = ch.name
      }
    },
    closeCreateModal() {
      this.showCreateModal = false
      this.modalDetection = null
      this.creating = false
    },
    async submitCreate() {
      if (!this.modalDetection) return
      // Defence-in-depth: button is gated by canSubmit, but in case it
      // gets bypassed (keyboard activation, race), revalidate here.
      if (!this.canSubmit) {
        const missing = []
        if (!this.form.title || !this.form.title.trim()) missing.push('Title')
        if (!this.form.mediaType) missing.push('Type')
        if (!this.form.channelId) missing.push('Channel')
        if (!this.form.supplier || !this.form.supplier.trim()) missing.push('Supplier')
        this.$buefy.toast.open({
          message: `Required: ${missing.join(', ')}`,
          type: 'is-warning',
          duration: 3000
        })
        return
      }
      const selectedChannel = this.availableChannels.find(
        c => c.id === this.form.channelId
      )
      this.creating = true
      try {
        const payload = {
          detection_id: this.modalDetection.detection_id,
          title: this.form.title.trim(),
          // Use form.mediaType (operator-confirmable) rather than this.mediaType
          // (tab default). Lets operator correct a misfiled detection — e.g. a
          // BUMPER found via the FILLER watcher → save under BUMPER folder
          // (per Issue 41 singular folder convention).
          media_type: this.form.mediaType,
          description: this.form.description || '',
          duration_ms: this.form.duration_ms || null,
          supplier: this.form.supplier.trim(),
          // Backend addChannels expects [{id, name, ...}] — pass the
          // selected channel as a single-element array. VOD fields stay
          // null so they inherit channel defaults (operator can edit per-
          // channel VOD via EditMedia later if needed).
          channels: selectedChannel
            ? [{ id: selectedChannel.id, name: selectedChannel.name }]
            : []
        }
        const result = await MediaApi.createFromDetection(payload)
        this.$buefy.toast.open({
          message: `Created ${this.formMediaTypeLabel} "${this.form.title.trim()}"`,
          type: 'is-success',
          duration: 4000
        })
        // Drop the actioned row from the local list — no need to re-fetch.
        this.uploads = this.uploads.filter(
          u => u.detection_id !== this.modalDetection.detection_id
        )
        // Tell parent so it can refresh the main media table.
        this.$emit('media-created', {
          mediaId: result.mediaId,
          mediaType: this.mediaType,
          title: this.form.title.trim()
        })
        this.closeCreateModal()
      } catch (err) {
        const msg = this._errorMessage(err)
        // Duplicate is a special case — surface the existing media id
        // so the operator knows where it went.
        if (err && err.data && err.data.err === 'E_DUPLICATE_MEDIA') {
          this.$buefy.toast.open({
            message: `Already exists: ${err.data.message || msg}`,
            type: 'is-warning',
            duration: 6000
          })
        } else {
          this.$buefy.toast.open({
            message: 'Create failed — ' + msg,
            type: 'is-danger',
            duration: 5000
          })
        }
      } finally {
        this.creating = false
      }
    },
    formatRelative(isoDate) {
      if (!isoDate) return ''
      const then = new Date(isoDate).getTime()
      if (Number.isNaN(then)) return ''
      const seconds = Math.floor((Date.now() - then) / 1000)
      if (seconds < 60) return `${seconds}s ago`
      const minutes = Math.floor(seconds / 60)
      if (minutes < 60) return `${minutes}m ago`
      const hours = Math.floor(minutes / 60)
      if (hours < 24) return `${hours}h ago`
      const days = Math.floor(hours / 24)
      if (days < 30) return `${days}d ago`
      return new Date(isoDate).toLocaleDateString()
    },
    _errorMessage(err) {
      if (!err) return 'unknown error'
      if (err.data && (err.data.detail || err.data.err)) {
        return err.data.detail || err.data.err
      }
      if (err.message) return err.message
      return 'network error'
    },
    // Single source of truth for the labels shown in the modal header,
    // warning banner, success toast, and elsewhere. Keep in sync with
    // the Type dropdown options.
    _labelForType(type) {
      const labels = {
        promo: 'Promo',
        filler: 'Filler',
        tvc: 'TVC',
        gfx: 'GFX',
        bumper: 'Bumper'
      }
      return labels[type] || type
    }
  }
}
</script>

<style scoped>
.unmatched-uploads-panel {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  margin-bottom: 16px;
  background: #fafafa;
}
.panel-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  cursor: pointer;
  user-select: none;
  font-size: 13px;
}
.panel-header:hover {
  background: #f0f0f0;
}
.panel-header.is-empty {
  opacity: 0.7;
}
.chevron {
  font-size: 10px;
  color: #6b7280;
  width: 12px;
}
.header-hint {
  color: #9ca3af;
  font-size: 11px;
  margin-left: 8px;
}
.ml-auto {
  margin-left: auto;
}
.panel-body {
  border-top: 1px solid #e5e7eb;
  padding: 8px 14px 12px;
  background: white;
}
.unmatched-table {
  width: 100%;
  font-size: 12px;
  border-collapse: collapse;
}
.unmatched-table th {
  text-align: left;
  font-weight: 600;
  color: #6b7280;
  padding: 6px 8px;
  border-bottom: 1px solid #e5e7eb;
}
.unmatched-table th.num,
.unmatched-table td.num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.unmatched-table td {
  padding: 6px 8px;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: middle;
}
.unmatched-table tr:last-child td {
  border-bottom: none;
}
.filename {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 11px;
  color: #1f2937;
  background: #f9fafb;
  padding: 2px 6px;
  border-radius: 3px;
}
.modal-content-inner {
  padding: 4px;
}
.has-margin-bottom-10 {
  margin-bottom: 10px;
}
</style>
